/** System user roles (stored in users.role) */
export const USER_ROLES = [
  'admin',
  'hr',
  'finance',
  'audit',
  'secretary',
  'employee',
] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const PAYROLL_PROCESS_ROLES: UserRole[] = ['admin', 'hr', 'finance'];

export const PAYROLL_APPROVAL_ROLES: UserRole[] = [
  'admin',
  'hr',
  'finance',
  'audit',
  'secretary',
];

export function isUserRole(value: string): value is UserRole {
  return (USER_ROLES as readonly string[]).includes(value);
}

/** Normalize role strings from API (handles legacy / partial values). */
export function normalizeUserRole(role: string | undefined | null): UserRole {
  if (!role) return 'employee';
  const r = role.toLowerCase().trim();
  if (isUserRole(r)) return r;
  if (r.includes('admin')) return 'admin';
  if (r.includes('secretary')) return 'secretary';
  if (r.includes('audit')) return 'audit';
  if (r.includes('finance')) return 'finance';
  if (r.includes('hr')) return 'hr';
  return 'employee';
}

export function getRoleLabel(role: string): string {
  switch (normalizeUserRole(role)) {
    case 'admin':
      return 'Administrator';
    case 'hr':
      return 'HR Manager';
    case 'finance':
      return 'Finance Officer';
    case 'audit':
      return 'Internal Auditor';
    case 'secretary':
      return 'Council Secretary';
    case 'employee':
      return 'Employee';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
}

export function isPayrollApprovalOnlyRole(role: string): boolean {
  const r = normalizeUserRole(role);
  return r === 'audit' || r === 'secretary';
}

export function canProcessPayroll(role: string): boolean {
  return PAYROLL_PROCESS_ROLES.includes(normalizeUserRole(role));
}

/** Whether the user may amend payslips before final approval (admin, hr, finance). */
export function canAmendPayroll(
  role: string,
  approvalStatus: string | undefined
): boolean {
  if (!approvalStatus || approvalStatus === 'approved') {
    return false;
  }
  return ['admin', 'hr', 'finance'].includes(normalizeUserRole(role));
}

/** Whether the user may rerun payroll for a period (admin, hr, finance). */
export function canRerunPayroll(
  role: string,
  approvalStatus: string | undefined
): boolean {
  if (!approvalStatus || approvalStatus === 'approved') {
    return false;
  }
  return canProcessPayroll(role);
}

/** Whether the user may click Approve for the current payroll approval status.
 * Order: HR → Finance → Audit → Secretary
 */
export function canApprovePayrollStep(role: string, approvalStatus: string): boolean {
  const r = normalizeUserRole(role);
  switch (approvalStatus) {
    case 'draft':
    case 'pending_hr':
      return r === 'hr' || r === 'admin';
    case 'pending_finance':
      return r === 'finance' || r === 'admin';
    case 'pending_audit':
      return r === 'audit' || r === 'admin';
    case 'pending_final':
      return r === 'secretary' || r === 'admin';
    default:
      return false;
  }
}

export const ROLE_SELECT_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Administrator' },
  { value: 'hr', label: 'HR Manager' },
  { value: 'finance', label: 'Finance Officer' },
  { value: 'audit', label: 'Internal Auditor' },
  { value: 'secretary', label: 'Council Secretary' },
  { value: 'employee', label: 'Employee' },
];
